import { StoreSummary } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from '../lib/axios';

// Types for the unified flow
export interface PostLoginFlowParams {
  user: any;
  stores: any[];
  invitations: any[];
  currentShop: string | null;
  navigate: any;
  selectStore: (shop: string) => Promise<void>;
  refreshStores?: () => Promise<void>;
  allStores?: any[];
}

export interface FlowResult {
  action: 'redirect' | 'stay' | 'error';
  destination?: string;
  message?: string;
}

/**
 * Centralized post-login flow handler
 * Implements the unified decision tree for store selection and redirection
 */
export async function handlePostLoginFlow({
  user,
  stores,
  invitations = [],
  currentShop,
  navigate,
  selectStore,
  refreshStores,
  allStores,
}: PostLoginFlowParams): Promise<FlowResult> {
  console.log('🔍 handlePostLoginFlow called:', {
    userId: user?.id,
    userEmail: user?.email,
    userSkip: user?.skip,
    storesLength: stores.length,
    invitationsLength: invitations.length,
    currentShop,
    userRole: user?.advanced_settings?.role
  });

  // Step 1: Fetch pending invitations if not provided
  // Use invitations passed from usePostLoginFlow (already loaded)
  let pendingInvitations = invitations;

  // Helper function to determine user role
  const getUserRole = (): 'owner' | 'admin' | 'member' | 'unknown' => {
    console.log('getUserRole - stores:', stores.map(s => ({ id: s.id, shop: s.shop, user_role: s.user_role })));
    
    if (stores.length === 0) {
      console.log('No stores found, returning unknown role');
      return 'unknown';
    }
    
    // Check if user has any owner role
    const hasOwnerRole = stores.some(store => store.user_role === 'owner');
    console.log('Has owner role:', hasOwnerRole);
    if (hasOwnerRole) return 'owner';
    
    // Check if user has any admin role
    const hasAdminRole = stores.some(store => store.user_role === 'admin');
    console.log('Has admin role:', hasAdminRole);
    if (hasAdminRole) return 'admin';
    
    // Check if user has any member role
    const hasMemberRole = stores.some(store => store.user_role === 'member');
    console.log('Has member role:', hasMemberRole);
    if (hasMemberRole) return 'member';
    
    // Default to unknown
    console.log('Defaulting to unknown role');
    return 'unknown';
  };

  // Step 1: Check for last selected store (for existing users)
  const lastSelectedStoreId = user?.last_selected_store_id;
  const lastSelectedStore = lastSelectedStoreId ? stores.find(s => s.id.toString() === lastSelectedStoreId.toString()) : null;

  // First-time connection onboarding (store owners with at least one store)
  const connectionOnboardingDone = Number(user?.skip) === 1;
  const roleForOnboarding = getUserRole();
  
  // Check if Shopify is connected for the current/selected store
  const getCurrentStore = () => {
    if (currentShop) return stores.find(s => s.shop === currentShop);
    if (lastSelectedStore) return stores.find(s => s.id.toString() === lastSelectedStore.id.toString());
    return stores.length > 0 ? stores[0] : null;
  };
  
  const currentStore = getCurrentStore();
  const shopifyConnected = !!(
    currentStore?.connected && 
    currentStore?.shop !== 'temp-store' &&
    (currentShop || lastSelectedStore?.shop || stores[0]?.shop)
  );
  
  console.log('First-time connection check:', {
    connectionOnboardingDone,
    roleForOnboarding,
    storesLength: stores.length,
    shopifyConnected,
    currentStore: currentStore?.shop,
    userSkip: user?.skip
  });
  
  // Check if user is coming from invitation flow
  const hasInvitationToken = localStorage.getItem('invitation_token') || 
                            sessionStorage.getItem('invitation_token') ||
                            // Also check if there are pending invitations (user just accepted one)
                            pendingInvitations.length > 0 ||
                            // Check if user was recently invited (check URL params or recent invitation acceptance)
                            window.location.search.includes('invite');
  
  console.log('Invitation flow check:', {
    hasInvitationToken,
    pendingInvitationsLength: pendingInvitations.length,
    urlParams: window.location.search
  });
  
  // Check if user has any connected stores (excluding temp-store)
  const hasConnectedStores = stores.some(store => 
    store.connected === true && store.shop !== 'temp-store'
  );
  
  console.log('🔍 Store connection check:', {
    storesLength: stores.length,
    hasConnectedStores,
    stores: stores.map(s => ({ shop: s.shop, connected: s.connected }))
  });
  
  // Check if user has member/admin access to any store
  const userRole = getUserRole();
  const hasMemberOrAdminAccess = userRole === 'member' || userRole === 'admin';
  
  // If user has member/admin access to any store, redirect to scaleboard directly
  if (hasMemberOrAdminAccess && stores.length > 0) {
    // Find the best store for this user (prioritize connected stores)
    const targetStore = stores.find(s => s.connected && s.shop !== 'temp-store') || stores[0];
    const shop = targetStore?.shop || null;
    
    if (shop) {
      console.log('🚀 Redirecting member/admin user to scaleboard:', {
        role: userRole,
        targetStore: shop,
        stores: stores.map(s => ({ shop: s.shop, role: s.user_role, connected: s.connected }))
      });
      
      // Store selection will be handled by AuthContext.applyStores
      return { action: 'redirect', destination: '/neo-intelligence' };
    }
  }

  // Show Connectionv1 for first-time users who haven't completed onboarding (skip = 0)
  // OR users who have no connected stores (regardless of skip status)
  // BUT NOT if they're coming from invitation flow OR if they are members/admins
  // This check should happen BEFORE any store selection logic
  if ((!connectionOnboardingDone || !hasConnectedStores) && !hasInvitationToken && !hasMemberOrAdminAccess) {
    const shop =
      currentShop ||
      lastSelectedStore?.shop ||
      (stores.length > 0 ? stores[0]?.shop : null) ||
      null;
    const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
    console.log('🚀 Redirecting to onboarding - user needs to complete connection setup or has no connected stores:', {
      role: userRole,
      shop,
      connectionOnboardingDone,
      shopifyConnected,
      hasInvitationToken,
      hasConnectedStores,
      storesLength: stores.length,
      skip: user?.skip,
      userId: user?.id,
      userEmail: user?.email
    });
    return { action: 'redirect', destination: `/on-boarding${q}` };
  }

  // ADDITIONAL CHECK: Force onboarding for users with skip = 0, regardless of other conditions
  // This ensures users who need onboarding never get to store selection
  // IMPORTANT: Skip this redirect if the user already has connected stores,
  // because the connection step was already completed (e.g., via WooCommerce billing flow)
  const hasCompletedConnection = hasConnectedStores && shopifyConnected;
  const needsOnboarding = Number(user?.skip) === 0 && !hasCompletedConnection;
  if (needsOnboarding && !hasInvitationToken) {
    const shop =
      currentShop ||
      lastSelectedStore?.shop ||
      (stores.length > 0 ? stores[0]?.shop : null) ||
      null;
    const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
    console.log('🚀 FORCE Redirecting to onboarding - user has skip = 0:', {
      skip: user?.skip,
      needsOnboarding,
      hasCompletedConnection,
      hasConnectedStores,
      shopifyConnected,
      hasInvitationToken,
      roleForOnboarding,
      userId: user?.id,
      userEmail: user?.email
    });
    return { action: 'redirect', destination: `/on-boarding${q}` };
  }

  // Step 2: Check current active store
  const activeStoreId = localStorage.getItem('activeStoreId');
  const activeStoreShop = localStorage.getItem('activeStoreShop');
  
  // Case A: User has a last selected store (restore from database)
  if (lastSelectedStore && !activeStoreId) {
    console.log('Case A: Checking last selected store:', lastSelectedStore.shop);
    
    // Verify user still has access to this store
    const storeInUserList = stores.find(s => s.id.toString() === lastSelectedStore.id.toString());
    if (!storeInUserList) {
      console.log('Last selected store not found in user stores, clearing it');
      // Clear the invalid last selected store
      localStorage.removeItem('activeStoreId');
      localStorage.removeItem('activeStoreShop');
      localStorage.removeItem('activeStore');
      localStorage.removeItem('shop');
      
      // After clearing invalid store, check if user has any stores left
      if (stores.length === 0) {
        console.log('No stores left after clearing invalid last selected store, checking for invites');
        // User lost access to all stores, check invites and redirect appropriately
        if (pendingInvitations.length > 0) {
          const needsOnboarding = Number(user?.skip) === 0;
          if (needsOnboarding && !hasInvitationToken) {
            console.log('User needs onboarding, redirecting to /on-boarding instead of stores');
            return { action: 'redirect', destination: '/on-boarding' };
          }
          console.log('User has pending invites, redirecting to stores');
          return { action: 'redirect', destination: '/on-boarding' };
        } else {
          console.log('User has no stores and no invites, checking onboarding');
          const needsOnboarding = Number(user?.skip) === 0;
          const userRole = getUserRole(); // Use enhanced role detection
          const hasMemberOrAdminAccess = userRole === 'member' || userRole === 'admin';
          if (needsOnboarding && !hasMemberOrAdminAccess) {
            return { action: 'redirect', destination: '/on-boarding' };
          } else {
            if (userRole === 'owner') {
              return { action: 'redirect', destination: '/billing/choose-plan' };
            } else {
              return { 
                action: 'redirect', 
                destination: '/on-boarding',
                message: 'No stores assigned. Please contact your store administrator.' 
              };
            }
          }
        }
      }
      // Continue to other cases if stores still exist
    } else {
      console.log('Case A: Restoring last selected store:', lastSelectedStore.shop);
      
      // Restore last selected store
      localStorage.setItem('activeStoreId', lastSelectedStore.id.toString());
      localStorage.setItem('activeStoreShop', lastSelectedStore.shop);
      localStorage.setItem('activeStore', JSON.stringify(lastSelectedStore));
      
      // Check if last selected store is connected
      if (!lastSelectedStore.connected && !hasInvitationToken) {
        console.log('🚀 Case A: Last selected store not connected, redirecting to /on-boarding');
        const shop = lastSelectedStore.shop;
        const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
        return { action: 'redirect', destination: `/on-boarding${q}` };
      }
      
      // Select the store
      await selectStore(lastSelectedStore.shop);
      
      // Redirect to scaleboard (user has a selected store)
      const destination = '/neo-intelligence';
      console.log('Case A - Restored last selected store, Destination:', destination);
      return { action: 'redirect', destination };
    }
  }
  
  // Case B: User has current active store (from localStorage)
  if (activeStoreId && activeStoreShop) {
    const explicitStore = stores.find(s => s.id.toString() === activeStoreId);
    
    if (!explicitStore) {
      console.log('Active store from localStorage not found in user stores, clearing it');
      // Clear the invalid active store
      localStorage.removeItem('activeStoreId');
      localStorage.removeItem('activeStoreShop');
      localStorage.removeItem('activeStore');
      localStorage.removeItem('shop');
      
      // After clearing invalid store, check if user has any stores left
      if (stores.length === 0) {
        console.log('No stores left after clearing invalid active store, checking for invites');
        // User lost access to all stores, check invites and redirect appropriately
        if (pendingInvitations.length > 0) {
          const needsOnboarding = Number(user?.skip) === 0;
          if (needsOnboarding && !hasInvitationToken) {
            console.log('User needs onboarding, redirecting to /on-boarding instead of stores');
            return { action: 'redirect', destination: '/on-boarding' };
          }
          console.log('User has pending invites, redirecting to stores');
          return { action: 'redirect', destination: '/on-boarding' };
        } else {
          console.log('User has no stores and no invites, checking onboarding');
          const needsOnboarding = Number(user?.skip) === 0;
          const userRole = getUserRole(); // Use enhanced role detection
          const hasMemberOrAdminAccess = userRole === 'member' || userRole === 'admin';
          if (needsOnboarding && !hasMemberOrAdminAccess) {
            return { action: 'redirect', destination: '/on-boarding' };
          } else {
            if (userRole === 'owner') {
              return { action: 'redirect', destination: '/billing/choose-plan' };
            } else {
              return { 
                action: 'redirect', 
                destination: '/on-boarding',
                message: 'No stores assigned. Please contact your store administrator.' 
              };
            }
          }
        }
      }
      // Continue to other cases if stores still exist
    } else {
      // Valid store found, select it and redirect based on role
      localStorage.setItem('activeStoreShop', explicitStore.shop);
      localStorage.setItem('activeStore', JSON.stringify(explicitStore));
      
      if (explicitStore.shop !== currentShop) {
        await selectStore(explicitStore.shop);
      }
      
      // Check if explicit store is connected before redirecting
      if (!explicitStore.connected && !hasInvitationToken) {
        console.log('🚀 Case B: Active store not connected, redirecting to /on-boarding');
        const shop = explicitStore.shop;
        const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
        return { action: 'redirect', destination: `/on-boarding${q}` };
      }
      
      // Determine role and redirect accordingly
      const currentUserRole = getUserRole();
      
      // For members, check if they have a selected store
      if (currentUserRole === 'owner') {
        const destination = '/neo-intelligence';
        console.log('Case B - Owner role, Destination:', destination);
        return { action: 'redirect', destination };
      } else if (currentUserRole === 'member') {
        // Check if member has a selected store
        const selectedStore = explicitStore || (stores.length > 0 ? stores[0] : null);
        
        if (selectedStore) {
          const destination = '/neo-intelligence';
          console.log('Case B - Member with selected store, Destination:', destination);
          return { action: 'redirect', destination };
        } else {
          // Check if user needs onboarding before redirecting to stores
          const needsOnboarding = Number(user?.skip) === 0;
          if (needsOnboarding && !hasInvitationToken) {
            console.log('Case B - Member needs onboarding, redirecting to /on-boarding instead of stores');
            return { action: 'redirect', destination: '/on-boarding' };
          }
          const destination = '/neo-intelligence';
          console.log('Case B - Member with no selected store, Destination:', destination);
          return { action: 'redirect', destination };
        }
      }
      // Check if user needs onboarding before redirecting to stores
      const needsOnboarding = Number(user?.skip) === 0;
      if (needsOnboarding && !hasInvitationToken) {
        console.log('Case B - Unknown role needs onboarding, redirecting to /on-boarding instead of stores');
        return { action: 'redirect', destination: '/on-boarding' };
      }
      const destination = '/neo-intelligence';
      console.log('Case B - Unknown role, Destination:', destination);
      return { action: 'redirect', destination };
    }
  }

  // Case B: Has only 1 active store
  if (stores.length === 1) {
    const singleStore = stores[0];
    console.log('Case B: Auto-selecting single store:', singleStore.shop);
    
    const currentUserRole = getUserRole();
    const hasMemberOrAdminAccess = currentUserRole === 'member' || currentUserRole === 'admin';
    
    // If user has member/admin access, redirect to scaleboard directly
    if (hasMemberOrAdminAccess) {
      console.log('🚀 Case B: Member/Admin with single store, redirecting to scaleboard');
      
      // Save to localStorage
      localStorage.setItem('activeStoreId', singleStore.id.toString());
      localStorage.setItem('activeStoreShop', singleStore.shop);
      localStorage.setItem('activeStore', JSON.stringify(singleStore));
      
      await selectStore(singleStore.shop);
      return { action: 'redirect', destination: '/neo-intelligence' };
    }
    
    // Check if this is a fresh user who needs onboarding
    const needsOnboarding = Number(user?.skip) === 0;
    const userRole = user?.advanced_settings?.role;
    
    console.log('Case B - Checking onboarding for single store:', {
      needsOnboarding,
      userRole,
      hasInvitationToken,
      userSkip: user?.skip
    });
    
    // FORCE CHECK: Users with skip = 0 MUST go to onboarding, no exceptions
    // ALSO check if single store is connected
    if (needsOnboarding && !hasInvitationToken) {
      console.log('🚀 Case B: FORCE - User with single store needs onboarding, redirecting to /on-boarding');
      const shop = singleStore.shop;
      const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
      return { action: 'redirect', destination: `/on-boarding${q}` };
    }
    
    // Check if single store is connected
    if (!singleStore.connected && !hasInvitationToken) {
      console.log('🚀 Case B: Single store not connected, redirecting to /on-boarding');
      const shop = singleStore.shop;
      const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
      return { action: 'redirect', destination: `/on-boarding${q}` };
    }
    
    // Priority 3: If user has invite token, let the invite flow handle it
    if (hasInvitationToken) {
      console.log('📋 Case B: User with single store has invite token, letting invite flow handle it');
      return { action: 'stay', message: 'Waiting for invitation flow to process' };
    }
    
    // Save to localStorage only if not going to onboarding
    localStorage.setItem('activeStoreId', singleStore.id.toString());
    localStorage.setItem('activeStoreShop', singleStore.shop);
    localStorage.setItem('activeStore', JSON.stringify(singleStore));
    
    await selectStore(singleStore.shop);
    
    // Redirect based on user role (only for users who don't need onboarding)
    const destination = '/neo-intelligence';
    console.log('Case B - Current user role:', currentUserRole, 'Destination:', destination);
    return { action: 'redirect', destination };
  }

  // Case C: Has multiple stores
  if (stores.length > 1) {
    console.log('Case C: Multiple stores, checking onboarding');
    
    const currentUserRole = getUserRole();
    const hasMemberOrAdminAccess = currentUserRole === 'member' || currentUserRole === 'admin';
    
    // If user has member/admin access, redirect to scaleboard directly
    if (hasMemberOrAdminAccess) {
      console.log('🚀 Case C: Member/Admin with multiple stores, redirecting to scaleboard');
      
      // Find the best store (prioritize connected stores)
      const targetStore = stores.find(s => s.connected && s.shop !== 'temp-store') || stores[0];
      
      // Save to localStorage
      localStorage.setItem('activeStoreId', targetStore.id.toString());
      localStorage.setItem('activeStoreShop', targetStore.shop);
      localStorage.setItem('activeStore', JSON.stringify(targetStore));
      
      await selectStore(targetStore.shop);
      return { action: 'redirect', destination: '/neo-intelligence' };
    }
    
    // Check if this is a fresh user who needs onboarding
    const needsOnboarding = Number(user?.skip) === 0;
    const userRole = user?.advanced_settings?.role;
    
    console.log('Case C - Checking onboarding for multiple stores:', {
      needsOnboarding,
      userRole,
      hasInvitationToken,
      userSkip: user?.skip
    });
    
    // FORCE CHECK: Users with skip = 0 MUST go to onboarding, no exceptions
    if (needsOnboarding && !hasInvitationToken) {
      console.log('🚀 Case C: FORCE - User with multiple stores needs onboarding, redirecting to /on-boarding');
      const shop = stores[0]?.shop || null;
      const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
      return { action: 'redirect', destination: `/on-boarding${q}` };
    }
    
    // Check if current store is connected for multiple stores
    if (!shopifyConnected && !hasInvitationToken) {
      console.log('🚀 Case C: Current store not connected, redirecting to /on-boarding');
      const shop = currentShop || stores[0]?.shop || null;
      const q = shop ? `?shop=${encodeURIComponent(shop)}` : '';
      return { action: 'redirect', destination: `/on-boarding${q}` };
    }
    
    // Priority 3: If user has invite token, let the invite flow handle it
    if (hasInvitationToken) {
      console.log('📋 Case C: User with multiple stores has invite token, letting invite flow handle it');
      return { action: 'stay', message: 'Waiting for invitation flow to process' };
    }
    
    // For owners with multiple stores, redirect to scaleboard instead of store hub
    const destination = '/neo-intelligence';
    console.log('Case C: Multiple stores, current user role:', currentUserRole, 'Destination:', destination);
    return { action: 'redirect', destination };
  }

  // Case D: No stores BUT has pending invites
  if (stores.length === 0 && pendingInvitations.length > 0) {
    // Check if user needs onboarding first
    const needsOnboarding = Number(user?.skip) === 0;
    if (needsOnboarding && !hasInvitationToken) {
      console.log('Case D: User needs onboarding, redirecting to /on-boarding instead of stores');
      return { action: 'redirect', destination: '/on-boarding' };
    }
    console.log('Case D: No stores but has pending invites, redirecting to store hub');
    return { action: 'redirect', destination: '/on-boarding' };
  }

  // Case E: No stores & no invites
  if (stores.length === 0 && pendingInvitations.length === 0) {
    console.log('=== CASE E: No stores & no invites ===');
    console.log('User data:', {
      userId: user?.id,
      userEmail: user?.email,
      skip: user?.skip,
      stores: stores.length,
      pendingInvitations: pendingInvitations.length,
      hasInvitationToken
    });
    
    const userRole = user?.advanced_settings?.role || 'member';
    
    // Check if user needs onboarding (skip = 0 means needs onboarding)
    const needsOnboarding = Number(user?.skip) === 0;
    
    console.log('Case E: No stores, checking onboarding:', {
      needsOnboarding,
      userSkip: user?.skip,
      userRole,
      hasInvitationToken
    });
    
    // FORCE CHECK: Users with skip = 0 MUST go to onboarding, no exceptions
    if (needsOnboarding && !hasInvitationToken) {
      console.log('🚀 Case E: FORCE - User with no stores needs onboarding, redirecting to /on-boarding');
      return { action: 'redirect', destination: '/on-boarding' };
    }
    
    // Priority 3: If user has invite token, let the invite flow handle it (don't redirect to onboarding)
    if (hasInvitationToken) {
      console.log('📋 User has invitation token, letting invite flow handle it');
      return { action: 'stay', message: 'Waiting for invitation flow to process' };
    }
    
    // Priority 4: Handle users who don't need onboarding
    if (!needsOnboarding) {
      // Check if user has member or admin access to any store
      const userRole = getUserRole();
      const hasMemberOrAdminAccess = userRole === 'member' || userRole === 'admin';
      
      if (hasMemberOrAdminAccess) {
        // If user has member/admin access, they should NOT see choose plan
        // Redirect them to scaleboard or appropriate page
        console.log('🚀 Member/Admin user skipping choose plan, redirecting to scaleboard');
        return { action: 'redirect', destination: '/neo-intelligence' };
      } else if (userRole === 'owner') {
        // If OWNER: show plan selection flow
        console.log('👑 Owner user who skipped onboarding, redirecting to choose plan');
        return { action: 'redirect', destination: '/billing/choose-plan' };
      } else {
        // If no special role: show empty state
        console.log('👤 User with no special role, redirecting to stores');
        return { 
          action: 'redirect', 
          destination: '/on-boarding',
          message: 'No stores assigned. Please contact your store administrator.' 
        };
      }
    }
    
    // Fallback: If none of the above conditions match, go to onboarding
    console.log('🚀 Fallback: Redirecting to onboarding');
    return { action: 'redirect', destination: '/on-boarding' };
  }

  return { action: 'stay', message: 'No action needed' };
}

/**
 * Handle store selection with proper state management
 */
export async function handleStoreSelection(
  store: StoreSummary,
  selectStore: (shop: string) => Promise<void>,
  navigate: (path: string) => void,
  allStores: StoreSummary[] = []
): Promise<void> {
  try {
    console.log('handleStoreSelection called:', store.shop);
    
    // Save to localStorage
    localStorage.setItem('activeStoreId', store.id.toString());
    localStorage.setItem('activeStoreShop', store.shop);
    localStorage.setItem('activeStore', JSON.stringify(store));
    
    // Also update AuthContext's shop key for compatibility
    localStorage.setItem('shop', store.shop);
    
    // Select store
    await selectStore(store.shop);
    
    // Trigger store-specific data refresh
    window.dispatchEvent(new CustomEvent('storeChanged', { detail: store }));
    
    // Check user permissions to decide where to navigate
    console.log('Checking permissions for store:', store.shop);
    
    try {
      // Fetch user permissions for the current store
      const permissionsResponse = await axios.get(`/user/permissions?shop=${encodeURIComponent(store.shop)}`);
      const permissions = permissionsResponse.data.permissions || {};
      
      console.log('User permissions:', permissions);
      
      // Check if user has scaleboard_access
      const hasScaleboardAccess = permissions.scaleboard_access || false;
      
      if (hasScaleboardAccess) {
        // User has scaleboard access, navigate to scaleboard
        console.log('User has scaleboard access, redirecting to scaleboard');
        navigate('/neo-intelligence');
      } else {
        // User doesn't have scaleboard access, navigate to first available sight
        console.log('User does not have scaleboard access, redirecting to first sight');
        await redirectToFirstAvailableSight(navigate, allStores);
      }
    } catch (error) {
      console.error('Failed to check permissions, defaulting to scaleboard:', error);
      // If permission check fails, default to scaleboard and let SimplePermissionGuard handle it
      navigate('/neo-intelligence');
    }
  } catch (error) {
    console.error('Failed to select store:', error);
    throw error;
  }
}

/**
 * Redirect to the first available sight for the current store
 */
async function redirectToFirstAvailableSight(
  navigate: (path: string) => void,
  stores: StoreSummary[]
): Promise<void> {
  try {
    console.log('Fetching available sights for first available sight...');
    
    // Get the current shop from localStorage
    const currentShop = localStorage.getItem('activeStoreShop');
    if (!currentShop) {
      console.log('No active store found, checking if user has stores');
      // For fresh users with no stores, let them proceed to dashboard setup
      if (stores.length === 0) {
        console.log('Fresh user with no stores, proceeding to dashboard');
        return; // Let them proceed to dashboard setup
      }
      // Check if user needs onboarding before redirecting to stores
      // Get user data to check onboarding status
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const needsOnboarding = Number(userData?.skip) === 0;
      const hasInvitationToken = localStorage.getItem('invitation_token') || 
                                sessionStorage.getItem('invitation_token');
      
      if (needsOnboarding && !hasInvitationToken) {
        console.log('User needs onboarding, redirecting to /on-boarding instead of stores');
        navigate('/on-boarding');
        return;
      }
      
      console.log('User has stores but no active store, redirecting to scaleboard');
      navigate('/neo-intelligence');
      return;
    }
    
    // Fetch available sights
    const response = await axios.get(`/sights?shop=${encodeURIComponent(currentShop)}`);
    const data = response.data;
    const sights = data.sights || [];
    
    if (sights.length === 0) {
      console.log('No sights available, attempting to create default sight');
      
      // Try to create/load a default sight before redirecting to stores
      try {
        const response = await axios.get(`/sights/default?shop=${encodeURIComponent(currentShop)}`);
        const defaultSight = response.data;
        const tenantKey = import.meta.env.VITE_REACT_APP_TENANT || currentShop;
        const sightPath = `/${tenantKey}/sight/default`;
        console.log('Default sight created/loaded, redirecting to:', sightPath);
        navigate(sightPath);
        return;
      } catch (error) {
        console.log('Failed to create default sight, redirecting to dashboard');
      }
      
      // Check if user needs onboarding before redirecting to stores
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const needsOnboarding = Number(userData?.skip) === 0;
      const hasInvitationToken = localStorage.getItem('invitation_token') || 
                                sessionStorage.getItem('invitation_token');
      
      if (needsOnboarding && !hasInvitationToken) {
        console.log('User needs onboarding, redirecting to /on-boarding instead of stores');
        navigate('/on-boarding');
        return;
      }
      
      navigate('/neo-intelligence');
      return;
    }
    
    // Get the first available sight
    const firstSight = sights[0];
    const tenantKey = import.meta.env.VITE_REACT_APP_TENANT || currentShop;
    const identifier = firstSight.md5_hash || firstSight.slug;
    
    const sightPath = `/${tenantKey}/sight/${identifier}`;
    console.log('Redirecting to first available sight:', sightPath);
    
    navigate(sightPath);
  } catch (error) {
    console.error('Failed to redirect to first available sight:', error);
    navigate('/');
  }
}


/**
 * Handle invitation acceptance with proper flow
 */
export async function handleInvitationAcceptance(
  invitation: any,
  refreshStores: () => Promise<void>,
  navigate: (path: string) => void
): Promise<void> {
  try {
    console.log('handleInvitationAcceptance called:', invitation.token);
    
    // Accept invitation and ensure user gets proper role from invitation
    const response = await axios.post('/invitations/accept', { token: invitation.token });
    if (!response.data.success) {
      throw new Error('Failed to accept invitation');
    }
    
    // Store invitation details for user role assignment
    const invitationData = {
      message: response.data.message,
      storeName: response.data.store.name,
      role: response.data.role || 'member', // Default to member if not specified
      shop: response.data.store.shop
    };
    
    // Store invitation data for modal display (use the key that useInvitationModal expects)
    const modalData = {
      accepted: true,
      shop: response.data.store.shop,
      storeName: response.data.store.name,
      message: response.data.message,
      role: response.data.role || 'member',
      isError: false
    };
    localStorage.setItem('invitation_accepted', JSON.stringify(modalData));
    
    // Also store for later use during signup/login
    localStorage.setItem('invitation_data', JSON.stringify(invitationData));
    
    // Refresh stores to include the newly accepted one
    await refreshStores();
    
    // Mark connection onboarding as complete for invitation users (they shouldn't see Connectionv1)
    try {
      await axios.post('/user/complete-connection-onboarding');
      console.log('Connection onboarding marked as complete for invitation user');
    } catch (error) {
      console.warn('Failed to mark connection onboarding as complete:', error);
      // Don't fail the invitation flow if this fails
    }
    
    // Navigate to profile after accepting invitation
    navigate('/profile');
    
    // Show success notification
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('invitation_accepted', {
        detail: invitationData
      }));
    }
  } catch (error) {
    console.error('Failed to accept invitation:', error);
  }
}

export function clearStoreData(): void {
  console.log('Clearing store data...');
  
  // Clear store-specific data from localStorage
  const storeKeys = [
    'sights',
    'dashboard_data',
    'settings',
    'permissions',
    'kpi_cache',
    'analytics_cache'
  ];
  
  storeKeys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  // Trigger store data cleared event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('storeDataCleared'));
  }
}

export function getUserRole(stores: StoreSummary[]): string {
  // Get user role from the first store (if any stores exist)
  const userStore = stores[0];
  if (userStore) {
    return userStore.user_role || 'member';
  }
  return 'member'; // Default to member if no stores or no role specified
}

export function singleStore(stores: StoreSummary[]): StoreSummary | null {
  // Get the single store (first store if any stores exist)
  return stores[0] || null;
}

/**
 * Check if user should be redirected to store hub
 */
export function shouldShowStoreHub(stores: StoreSummary[], invitations: any[]): boolean {
  const hasStores = stores.length > 0;
  const hasInvitations = invitations.length > 0;
  const hasMultipleStores = stores.length > 1;
  
  return !hasStores || hasInvitations || hasMultipleStores;
}
